import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, apiErr } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { BocauxMenu } from "./BocauxMenu";
import { toast } from "sonner";
import { ShieldCheck, Info } from "lucide-react";
import AvailabilityCalendar, { toISO, fromISO } from "./AvailabilityCalendar";
import { ReassuranceBadges } from "./ReassuranceBadges";

const LATE_CHECKOUT_FEE = 20;

export default function BookingDialog({ item, accommodations = [], packageName = null, prefill = {}, open, onOpenChange }) {
  const { t, lang } = useI18n();
  const [picked, setPicked] = useState("");
  const [form, setForm] = useState({
    arrivee: prefill.arrivee || "", depart: prefill.depart || "",
    voyageurs: prefill.voyageurs || 2, adultes: prefill.adultes || prefill.voyageurs || 2, enfants: prefill.enfants || 0,
    nom: "", email: "", telephone: "", message: "",
    adresse: "", code_postal: "", ville: "", pays: "France",
    late_checkout: false, pet: false, transfer: false, cancellation_insurance: false,
    bocaux_entree: 0, bocaux_plat: 0, bocaux_dessert: 0,
  });
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [sending, setSending] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [unavailable, setUnavailable] = useState([]);

  const active = item || accommodations.find((a) => a.id === picked) || null;

  const eur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n) + " €";

  const BOCAUX = { entree: 7, plat: 13, dessert: 7 };
  const isChambre = active?.type === "chambre";
  const bocauxTotal = Number(form.bocaux_entree) * BOCAUX.entree + Number(form.bocaux_plat) * BOCAUX.plat + Number(form.bocaux_dessert) * BOCAUX.dessert;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const label = "text-xs font-medium text-ink-600 mb-1 block";
  const input = "w-full rounded-xl border border-ink-400/25 bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-brand focus:ring-1 focus:ring-brand";

  const fetchQuote = useCallback(async () => {
    if (!active?.id || !form.arrivee || !form.depart || form.arrivee >= form.depart) { setQuote(null); return; }
    setLoadingQuote(true);
    try {
      const { data } = await api.post("/bookings/quote", {
        accommodation_id: active.id, arrivee: form.arrivee, depart: form.depart,
        adultes: Number(form.adultes), enfants: Number(form.enfants),
        late_checkout: form.late_checkout, pet: form.pet, transfer: form.transfer,
        cancellation_insurance: form.cancellation_insurance,
      });
      setQuote(data);
    } catch { setQuote(null); }
    finally { setLoadingQuote(false); }
  }, [active?.id, form.arrivee, form.depart, form.adultes, form.enfants, form.late_checkout, form.pet, form.transfer, form.cancellation_insurance]);

  useEffect(() => { if (open) fetchQuote(); }, [open, fetchQuote]);

  useEffect(() => {
    if (!open || !active?.id) { return; }
    api.get(`/accommodations/${active.id}/availability`)
      .then((r) => setUnavailable(r.data.unavailable || []))
      .catch(() => setUnavailable([]));
  }, [open, active?.id]);

  const nightsBetween = (from, to) => {
    const out = []; const d = new Date(from);
    while (d < to) { out.push(toISO(d)); d.setDate(d.getDate() + 1); }
    return out;
  };
  const handleRange = (r) => {
    if (!r || !r.from) { setForm((f) => ({ ...f, arrivee: "", depart: "" })); return; }
    if (r.from && r.to) {
      const bad = nightsBetween(r.from, r.to).some((d) => unavailable.includes(d));
      if (bad) { toast.error(t("bookingForm.rangeUnavailable")); setForm((f) => ({ ...f, arrivee: toISO(r.from), depart: "" })); return; }
    }
    setForm((f) => ({ ...f, arrivee: r.from ? toISO(r.from) : "", depart: r.to ? toISO(r.to) : "" }));
  };
  const range = form.arrivee ? { from: fromISO(form.arrivee), to: form.depart ? fromISO(form.depart) : undefined } : undefined;

  useEffect(() => {
    if (open && (prefill.arrivee || prefill.depart)) {
      setForm((f) => ({
        ...f,
        arrivee: prefill.arrivee || f.arrivee,
        depart: prefill.depart || f.depart,
        voyageurs: prefill.voyageurs || f.voyageurs,
        adultes: prefill.adultes || prefill.voyageurs || f.adultes,
        enfants: prefill.enfants != null ? prefill.enfants : f.enfants,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill.arrivee, prefill.depart, prefill.voyageurs, prefill.adultes, prefill.enfants]);

  const submit = async (e) => {
    e.preventDefault();
    if (!active) { toast.error(t("bookingForm.chooseAccommodation")); return; }
    if (!form.arrivee || !form.depart) { toast.error(t("bookingForm.selectDates")); return; }
    if (!quote?.available) { toast.error(t("bookingForm.unavailable")); return; }
    if (!acceptedTerms) { toast.error(t("bookingForm.termsRequired")); return; }
    const capMax = Number(active?.capacity) || 0;
    if (capMax && (Number(form.adultes) + Number(form.enfants)) > capMax) {
      toast.error(`Ce logement accueille au maximum ${capMax} voyageurs.`); return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        accommodation_id: active.id, arrivee: form.arrivee, depart: form.depart,
        voyageurs: Number(form.adultes) + Number(form.enfants), adultes: Number(form.adultes), enfants: Number(form.enfants),
        nom: form.nom, email: form.email,
        telephone: form.telephone, message: form.message, late_checkout: form.late_checkout,
        adresse: form.adresse, code_postal: form.code_postal, ville: form.ville, pays: form.pays,
        pet: form.pet, transfer: form.transfer, cancellation_insurance: form.cancellation_insurance,
        bocaux_entree: isChambre ? Number(form.bocaux_entree) : 0,
        bocaux_plat: isChambre ? Number(form.bocaux_plat) : 0,
        bocaux_dessert: isChambre ? Number(form.bocaux_dessert) : 0,
        accept_terms: acceptedTerms,
        package_name: packageName, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(apiErr(err));
      setSending(false);
    }
  };

  const row = (l, v, opts = {}) => (
    <div className={`flex items-center justify-between text-sm ${opts.strong ? "font-semibold text-ink-900" : "text-ink-600"}`}>
      <span>{l}</span><span className={opts.strong ? "" : "text-ink-900"}>{v}</span>
    </div>
  );

  const showPicker = !item && accommodations.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-ink-900">
            {t("bookingForm.title")}{active ? ` · ${active.name}` : ""}
          </DialogTitle>
        </DialogHeader>
        {packageName && (
          <div className="-mt-2 mb-1 inline-flex items-center gap-2 self-start rounded-full bg-brand-50 text-brand px-3 py-1 text-xs font-medium" data-testid="booking-package-tag">
            {t("packages.title")} : {packageName}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          {showPicker && (
            <div>
              <label className={label}>{t("bookingForm.chooseAccommodation")} *</label>
              <select value={picked} onChange={(e) => setPicked(e.target.value)} required className={input} data-testid="booking-accommodation-select">
                <option value="">—</option>
                {accommodations.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.type === "gite" ? t("common.gite") : t("common.chambre")})</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={label}>{t("bookingForm.calendarPick")}</label>
            <div className="rounded-xl border border-ink-400/25 bg-white p-2 flex justify-center" data-testid="booking-calendar">
              <AvailabilityCalendar lang={lang} unavailable={unavailable} range={range} onSelect={handleRange} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-500">
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-brand" /> {t("common.arrival")} / {t("common.departure")}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: "#b45309" }} /> {lang === "en" ? "Departure only" : "Départ possible"}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-red-100 border border-red-300" /> {t("bookingForm.unavailableLegend")}</span>
            </div>
            {form.arrivee && form.depart && (
              <p className="mt-2 text-sm font-medium text-ink-800" data-testid="booking-selected-dates">{form.arrivee} → {form.depart}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>{t("common.adults")}</label>
              <select value={form.adultes} onChange={set("adultes")} className={input} data-testid="booking-adultes">
                {Array.from({ length: Number(active?.capacity) || 8 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>{t("common.children")}</label>
              <select value={form.enfants} onChange={set("enfants")} className={input} data-testid="booking-enfants">
                {Array.from({ length: Math.max((Number(active?.capacity) || 8) - Number(form.adultes) + 1, 1) }, (_, i) => i).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          {active?.capacity ? <p className="-mt-1 text-xs text-ink-400" data-testid="booking-capacity-hint">Capacité maximale : {active.capacity} voyageurs.</p> : null}

          <div className="rounded-xl border border-black/5 bg-white p-3.5" data-testid="booking-extras">
            <p className="text-xs font-semibold text-ink-700 mb-2">{t("bookingForm.extras")}</p>
            {active?.type === "gite" && (active?.cleaning_fee || 0) > 0 && (
              <div className="flex items-center justify-between text-sm text-ink-600 mb-2" data-testid="booking-cleaning-fee">
                <span>{t("bookingForm.cleaningFee")} <span className="text-ink-400">· {t("bookingForm.cleaningFeeNote")}</span></span>
                <span className="font-medium text-ink-900">{active.cleaning_fee} €</span>
              </div>
            )}
            <label className="flex items-center justify-between text-sm text-ink-600 cursor-pointer">
              <span className="inline-flex items-center gap-2">
                <input type="checkbox" checked={form.late_checkout} onChange={(e) => setForm((f) => ({ ...f, late_checkout: e.target.checked }))} className="w-4 h-4 accent-brand" data-testid="booking-late-checkout" />
                {t("bookingForm.lateCheckout")}
              </span>
              <span className="font-medium text-ink-900">+{LATE_CHECKOUT_FEE} €</span>
            </label>
          </div>

          {isChambre && (
          <div className="rounded-xl border border-black/5 bg-white p-3.5" data-testid="booking-bocaux">
            <p className="text-xs font-semibold text-ink-700 mb-0.5">{t("bookingForm.bocaux")}</p>
            <p className="text-[11px] text-ink-400 mb-1">{t("bookingForm.bocauxNote")}</p>
            <div className="mb-2.5"><BocauxMenu className="text-xs" /></div>
            {[
              ["bocaux_entree", t("bookingForm.bocauxEntree"), BOCAUX.entree],
              ["bocaux_plat", t("bookingForm.bocauxPlat"), BOCAUX.plat],
              ["bocaux_dessert", t("bookingForm.bocauxDessert"), BOCAUX.dessert],
            ].map(([k, lbl, price]) => (
              <div key={k} className="flex items-center justify-between text-sm text-ink-600 mb-2">
                <span>{lbl} <span className="text-ink-400">· {price} €</span></span>
                <select value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }))} className="rounded-lg border border-ink-400/25 bg-white px-2 py-1 text-sm outline-none focus:border-brand" data-testid={`booking-${k.replace("_", "-")}`}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            ))}
            {bocauxTotal > 0 && (
              <div className="border-t border-black/5 pt-2 mt-1 flex items-center justify-between text-sm">
                <span className="text-ink-500">{t("bookingForm.bocauxEstimate")}</span>
                <span className="font-medium text-ink-900">{eur(bocauxTotal)}</span>
              </div>
            )}
          </div>
          )}

          {/* Assurance annulation facultative */}
          <div className={`rounded-xl border p-3.5 transition-colors ${form.cancellation_insurance ? "border-brand/40 bg-brand-50/40" : "border-black/5 bg-white"}`} data-testid="booking-insurance-block">
            <label className="flex items-start justify-between gap-3 cursor-pointer">
              <span className="inline-flex items-start gap-2">
                <input type="checkbox" checked={form.cancellation_insurance} onChange={(e) => setForm((f) => ({ ...f, cancellation_insurance: e.target.checked }))} className="w-4 h-4 accent-brand mt-0.5 shrink-0" data-testid="booking-insurance" />
                <span>
                  <span className="text-sm font-semibold text-ink-900 flex items-center gap-1.5"><ShieldCheck size={15} className="text-brand" /> {t("bookingForm.insurance")}</span>
                  <span className="block text-xs text-ink-500 mt-0.5">{t("bookingForm.insuranceDesc")}</span>
                </span>
              </span>
              <span className="text-sm font-semibold text-ink-900 whitespace-nowrap">+4 %</span>
            </label>
            <ul className="mt-2.5 pl-6 space-y-1">
              <li className="flex items-start gap-1.5 text-xs text-ink-600"><span className="text-brand mt-0.5">✓</span> {t("bookingForm.insuranceCase1")}</li>
              <li className="flex items-start gap-1.5 text-xs text-ink-600"><span className="text-brand mt-0.5">✓</span> {t("bookingForm.insuranceCase2")}</li>
            </ul>
            <p className="mt-2 pl-6 text-[11px] leading-snug text-ink-400">{t("bookingForm.insuranceExclusion")}</p>
          </div>

          {/* Récapitulatif tarifaire */}
          <div className="rounded-xl border border-brand/20 bg-brand-50/40 p-4 space-y-2" data-testid="booking-summary">
            {!active ? (
              <p className="text-sm text-ink-400">{t("bookingForm.chooseAccommodation")}</p>
            ) : loadingQuote ? (
              <p className="text-sm text-ink-400">{t("bookingForm.computing")}</p>
            ) : !quote ? (
              <p className="text-sm text-ink-400">{t("bookingForm.selectDates")}</p>
            ) : !quote.available ? (
              <p className="text-sm text-red-600 font-medium" data-testid="booking-unavailable">{t("bookingForm.unavailable")}</p>
            ) : (
              <>
                {row(`${t("bookingForm.subtotal")} · ${quote.nights} ${t("bookingForm.nights")}`, eur(quote.nightly_total))}
                {quote.cleaning_fee > 0 && row(t("bookingForm.cleaningFee"), eur(quote.cleaning_fee))}
                {quote.late_checkout_fee > 0 && row(t("bookingForm.lateCheckout"), eur(quote.late_checkout_fee))}
                {quote.insurance_fee > 0 && row(t("bookingForm.insurance"), eur(quote.insurance_fee))}
                <div className="border-t border-brand/15 pt-2">{row(t("bookingForm.total"), eur(quote.total), { strong: true })}</div>
                <div className="rounded-lg bg-brand text-white px-3 py-2 flex items-center justify-between text-sm font-semibold" data-testid="booking-deposit">
                  <span>{t("bookingForm.depositNow")}</span><span>{eur(quote.deposit)}</span>
                </div>
                {row(t("bookingForm.balance"), eur(quote.balance))}
                {quote.tourist_tax > 0 && row(t("bookingForm.touristTax"), eur(quote.tourist_tax))}
                <div className="flex items-center justify-between text-xs text-ink-400 pt-1">
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} /> {t("bookingForm.cautionLine")}</span>
                  <span>{eur(quote.caution)}</span>
                </div>
                <p className="flex items-start gap-1.5 text-xs text-ink-400 pt-1"><Info size={13} className="mt-0.5 shrink-0" /> {t("bookingForm.cancellation")}</p>
                <p className="flex items-start gap-1.5 text-xs text-ink-400"><Info size={13} className="mt-0.5 shrink-0" /> {t("bookingForm.paymentNote")}</p>
              </>
            )}
          </div>

          <div><label className={label}>{t("bookingForm.name")} *</label><input value={form.nom} onChange={set("nom")} required className={input} data-testid="booking-nom" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>{t("bookingForm.email")} *</label><input type="email" value={form.email} onChange={set("email")} required className={input} data-testid="booking-email" /></div>
            <div><label className={label}>{t("bookingForm.phone")}</label><input value={form.telephone} onChange={set("telephone")} className={input} data-testid="booking-tel" /></div>
          </div>
          <div><label className={label}>{t("bookingForm.message")}</label><textarea value={form.message} onChange={set("message")} rows={2} className={input} data-testid="booking-message" /></div>
          <div><label className={label}>{t("bookingForm.address")}</label><input value={form.adresse} onChange={set("adresse")} className={input} data-testid="booking-adresse" placeholder={t("bookingForm.addressPh")} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>{t("bookingForm.postalCode")}</label><input value={form.code_postal} onChange={set("code_postal")} className={input} data-testid="booking-cp" /></div>
            <div><label className={label}>{t("bookingForm.city")}</label><input value={form.ville} onChange={set("ville")} className={input} data-testid="booking-ville" /></div>
            <div><label className={label}>{t("bookingForm.country")}</label><input value={form.pays} onChange={set("pays")} className={input} data-testid="booking-pays" /></div>
          </div>

          <label className="flex items-start gap-2 text-xs text-ink-600 cursor-pointer">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="w-4 h-4 accent-brand mt-0.5 shrink-0" data-testid="booking-accept-terms" />
            <span>
              {t("bookingForm.acceptTerms")}{" "}
              <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-brand underline" data-testid="booking-view-cgv">
                {t("bookingForm.acceptTermsCgv")}
              </a>{" "}
              {t("bookingForm.acceptTermsAnd")}{" "}
              <a href="/annulation" target="_blank" rel="noopener noreferrer" className="text-brand underline" data-testid="booking-view-cancellation">
                {t("bookingForm.acceptTermsCancellation")}
              </a>
              {" "}·{" "}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contrat-exemple.pdf?type=${active?.type || "gite"}`} target="_blank" rel="noopener noreferrer" className="text-brand underline" data-testid="booking-view-contract">
                {t("bookingForm.viewContract")}
              </a>
            </span>
          </label>

          <button type="submit" disabled={sending || loadingQuote || !quote?.available || !acceptedTerms} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 transition-colors disabled:opacity-50" data-testid="booking-submit">
            {sending ? t("common.loading") : quote?.available ? `${t("bookingForm.payDeposit")} · ${eur(quote.deposit)}` : t("common.send")}
          </button>

          <p className="text-[11px] leading-snug text-ink-500 mt-2" data-testid="booking-noshow-notice">
            {t("bookingForm.noShowNotice")}
          </p>

          <ReassuranceBadges className="mt-4" />
        </form>
      </DialogContent>
    </Dialog>
  );
}
